import { preloadScreen } from '../screens/lazy';

type ResettableNavigation = {
  reset: (state: any) => void;
};

/**
 * CORE-1299 (QA 29.09.2026) — навигация, у которой `reset` делается, когда чанк
 * целевого экрана уже скачан.
 *
 * Экраны ленивые (CORE-638). Сброс на нескачанный экран оставляет адрес
 * прежним: React Navigation пишет его по состоянию навигатора, а тот, пока
 * экран в Suspense, отдаёт старое. Когда чанк доехал, нового события уже нет.
 * Вошедший из эфира видел комнату под адресом `/login?returnTo=…`, а
 * перезагрузка уводила его на главную.
 *
 * `isCurrent` — ещё ли нужен этот переход: пока чанк качается, экран входа мог
 * уйти или запланировать возврат заново.
 */
export function resetAfterPreload<T extends ResettableNavigation>(
  navigation: T,
  isCurrent: () => boolean,
  preload: (routeName: string) => Promise<void> = preloadScreen,
): T {
  return new Proxy(navigation, {
    get(target, prop, receiver) {
      if (prop === 'reset') {
        return (state: { index?: number; routes: { name: string }[] }) => {
          const focused = state.routes[state.index ?? state.routes.length - 1];
          void preload(focused.name).then(() => {
            if (isCurrent()) target.reset(state);
          });
        };
      }
      const value = Reflect.get(target, prop, receiver);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}
